import { useState } from 'react';
import { Link } from 'react-router-dom';
import TopNav from '../components/TopNav';
import SEO from '../components/SEO';
import Footer from '../components/Footer';
import api, { getApiError } from '../lib/axios';
import {
  Grid3X3, Search, Upload, Loader2, CheckCircle, XCircle, TrendingDown, AlertTriangle,
  Download, Globe, X
} from 'lucide-react';
import type { LookupResponse, LookupResultItem } from '../types';

export default function BulkCheck() {
  const [domainText, setDomainText] = useState('');
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [result, setResult] = useState<LookupResponse | null>(null);

  const domainCount = domainText.split('\n').map(d => d.trim()).filter(d => d.length > 0).length;

  const handleCheck = async () => {
    const domains = domainText.split('\n').map(d => d.trim()).filter(d => d.length > 0);
    if (domains.length === 0) { setError('Paste at least one domain'); return; }
    if (domains.length > 500) { setError('Maximum 500 domains per check'); return; }

    setLoading(true);
    setError('');
    setResult(null);
    setProgress(10);

    try {
      setProgress(30);
      const res = await api.post('/api/lookup/', { domains });
      setProgress(90);
      setResult(res.data);
      setProgress(100);
    } catch (err: any) {
      setError(getApiError(err, 'Bulk check failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      const lines = text.split('\n').map(line => line.split(',')[0]?.trim().replace(/"/g, '')).filter(d => d && d.length > 0 && !d.toLowerCase().includes('domain'));
      setDomainText(lines.join('\n'));
    };
    reader.readAsText(file);
  };

  const handleExport = async () => {
    if (!result) return;
    try {
      const response = await api.get(`/api/export/csv/${result.id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a'); a.href = url; a.setAttribute('download', `bulk_${result.id}.csv`);
      document.body.appendChild(a); a.click(); a.remove();
    } catch {}
  };

  const fmt = (n: number | null) => {
    if (n === null || n === undefined) return '-';
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n.toLocaleString();
  };

  const found = result?.results?.filter(r => r.seller_count > 0).length || 0;
  const notFound = (result?.total_queried || 0) - (result?.total_found || 0);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Bulk Domain Checker: Check Guest Post Prices for 500 Sites at Once"
        description="Check guest post and backlink prices for up to 500 domains at once. Upload CSV or paste your list. Free bulk domain checker and bulk backlink checker from LinkDiry."
        canonical="/bulk-checker"
      />
      <TopNav />

      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Input Panel */}
        {!result && (
          <div className="border-2 border-slate-200 rounded-2xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-brand-50 rounded-xl flex items-center justify-center">
                  <Grid3X3 className="w-5 h-5 text-brand-600" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-slate-900">Bulk Domain Check</h1>
                  <p className="text-sm text-slate-500">Paste up to 500 domains at once</p>
                </div>
              </div>
              <span className="px-3 py-1 bg-slate-100 rounded-full text-sm text-slate-500 font-medium">
                {domainCount} / 500 domains
              </span>
            </div>

            <textarea
              value={domainText}
              onChange={e => setDomainText(e.target.value)}
              placeholder={"forbes.com\nmsn.com\nusatoday.com\ntechcrunch.com\nentrepreneur.com"}
              rows={8}
              className="w-full rounded-xl border border-slate-200 bg-slate-50 text-slate-900 p-4 text-sm font-mono focus:ring-2 focus:ring-brand-500 focus:border-transparent resize-y mb-4"
            />

            <div className="flex items-center justify-between mb-4">
              <p className="text-xs text-slate-400 flex items-center gap-1">
                One domain per line · No http:// needed
              </p>
              <div className="flex gap-2">
                <label className="px-4 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 cursor-pointer flex items-center gap-1.5">
                  <Upload className="w-4 h-4" /> Upload File
                  <input type="file" accept=".csv,.txt,.xlsx" onChange={handleFileUpload} className="hidden" />
                </label>
              </div>
            </div>

            <div className="border-t border-slate-100 pt-4 flex items-center justify-end">
              <button onClick={handleCheck} disabled={loading || domainCount === 0}
                className="px-6 py-3 bg-brand-600 hover:bg-brand-700 text-white font-semibold rounded-xl transition-colors disabled:opacity-50 flex items-center gap-2 shadow-lg shadow-brand-500/20">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                {loading ? 'Checking...' : 'Check All Domains'}
              </button>
            </div>

            {error && <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">{error}</div>}

            {/* Progress Bar */}
            {loading && (
              <div className="mt-4">
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-brand-500 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
                </div>
                <p className="text-xs text-slate-400 mt-1">Checking domains... {progress}%</p>
              </div>
            )}
          </div>
        )}

        {/* Results */}
        {result && (
          <div className="space-y-6">
            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { icon: Search, label: 'Checked', value: result.total_queried, color: 'text-brand-600' },
                { icon: CheckCircle, label: 'Found', value: result.total_found, color: 'text-emerald-600' },
                { icon: TrendingDown, label: 'With Prices', value: found, color: 'text-amber-600' },
                { icon: XCircle, label: 'Not Found', value: notFound, color: 'text-red-500' },
              ].map(s => {
                const Icon = s.icon;
                return (
                  <div key={s.label} className="bg-white border border-slate-200 rounded-xl p-4">
                    <Icon className={`w-5 h-5 ${s.color} mb-2`} />
                    <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                    <p className="text-xs text-slate-500">{s.label}</p>
                  </div>
                );
              })}
            </div>

            {/* Export */}
            <div className="flex justify-end gap-2">
              <button onClick={handleExport} className="px-4 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 flex items-center gap-1.5">
                <Download className="w-4 h-4" /> Export CSV
              </button>
            </div>

            {/* Results Table */}
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/50">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase w-8">#</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Domain</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Best Price</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">DR</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Sellers</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {result.results.map((r, idx) => (
                    <tr key={r.domain} className="border-b border-slate-50 hover:bg-slate-50/50">
                      <td className="px-4 py-3 text-slate-400 text-xs">{idx + 1}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-500 uppercase">{r.domain.charAt(0)}</div>
                          <Link to={`/domain/${r.domain}`} className="font-medium text-slate-900 hover:text-brand-600">{r.domain}</Link>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {r.seller_count > 0 ? (
                          <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-xs font-medium">Found</span>
                        ) : (
                          <span className="px-2 py-0.5 bg-slate-100 text-slate-500 rounded text-xs">Not found</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {r.min_price !== null ? <span className="font-semibold text-slate-900">${r.min_price.toFixed(0)}</span> : <span className="text-slate-300"> - </span>}
                      </td>
                      <td className="px-4 py-3 text-slate-700">{r.dr ?? '-'}</td>
                      <td className="px-4 py-3">{r.seller_count > 0 ? <span className="px-2 py-0.5 bg-slate-100 rounded text-xs font-medium">{r.seller_count}</span> : ' - '}</td>
                      <td className="px-4 py-3">
                        {r.seller_count > 0 && <Link to={`/domain/${r.domain}`} className="text-brand-600 hover:underline text-xs font-medium">View →</Link>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <button onClick={() => { setResult(null); setDomainText(''); }} className="text-sm text-slate-500 hover:text-slate-700">
              ← Check more domains
            </button>
          </div>
        )}
      </div>

      {/* SEO Content Sections  -  Below the tool */}
      <div className="max-w-4xl mx-auto px-4 pb-16">
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Check 500 Domains in 10 Seconds</h2>
          <p className="text-slate-600 leading-relaxed mb-4">
            A bulk domain checker (also called a bulk backlink checker) lets you check guest post availability and pricing for hundreds of domains at once. Instead of searching one domain at a time, paste up to 500 domains and instantly see which ones have guest post opportunities, what they cost, and which sellers offer them. You can compare backlink prices across every marketplace in seconds.
          </p>
          <p className="text-slate-600 leading-relaxed">
            If you manage client campaigns, prospect link targets, or just have a big list of domains to check, doing it one at a time is a waste of time. This backlink price checker searches all 50+ marketplaces for every domain in your list. Free, no limits.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">How It Works: Four Steps</h2>
          <ol className="space-y-3 text-slate-600">
            {[
              { step: 'Prepare your domain list', desc: 'Paste domains one per line, or upload a CSV/TXT file. No need to include http:// or www.' },
              { step: 'Click "Check All Domains"', desc: 'We check every domain against 50+ guest post marketplaces in seconds.' },
              { step: 'Review the results', desc: 'See which domains have guest post opportunities, their best prices, DR, and how many sellers offer them.' },
              { step: 'Export or take action', desc: 'Export results as CSV, add domains to your watchlist, or click through to the cheapest seller.' },
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="w-7 h-7 bg-brand-50 rounded-lg flex items-center justify-center text-sm font-bold text-brand-600 flex-shrink-0">{i + 1}</span>
                <div><strong className="text-slate-900">{item.step}</strong>  -  {item.desc}</div>
              </li>
            ))}
          </ol>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Who Uses the Bulk Checker (and How)</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { title: 'Competitor backlink analysis', desc: 'Export your competitor\'s backlink profile from Ahrefs, upload the domains, and see which referring sites sell guest posts  -  and what they cost.' },
              { title: 'Prospecting at scale', desc: 'Have a list of 200 niche sites? Check them all at once to find which ones offer guest posts and compare prices across sellers.' },
              { title: 'Budget planning', desc: 'Upload your target domain list and instantly calculate the total cost of your link building campaign across different marketplaces.' },
              { title: 'Finding hidden deals', desc: 'Some domains are only available on 1-2 marketplaces. Bulk checking ensures you never miss a cheaper option on a marketplace you would not have checked.' },
            ].map(item => (
              <div key={item.title} className="border border-slate-200 rounded-xl p-4">
                <h3 className="font-semibold text-slate-900 mb-1 text-sm">{item.title}</h3>
                <p className="text-xs text-slate-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Bulk Checker Questions</h2>
          <div className="space-y-3">
            {[
              { q: 'How many domains can I check at once?', a: 'Up to 500 domains per bulk check. You can run unlimited bulk checks  -  there is no daily limit.' },
              { q: 'What file formats are supported?', a: 'You can upload .csv, .txt, or .xlsx files. Or simply paste domains one per line in the text area.' },
              { q: 'Is the bulk domain checker free?', a: 'Yes, completely free with no limits. No account required for basic checks. Create a free account to save results and set price alerts.' },
              { q: 'How fast is the bulk check?', a: 'Most checks complete in under 10 seconds for 100 domains. Larger lists (500 domains) typically finish within 30 seconds.' },
              { q: 'What data does the bulk checker show?', a: 'For each domain: guest post availability, best price, price range, DR, organic traffic, number of sellers, and quality score. Click any domain for full details.' },
            ].map((faq, i) => (
              <details key={i} className="group border border-slate-200 rounded-xl">
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer text-slate-900 font-semibold text-sm hover:text-brand-600">
                  <h3>{faq.q}</h3>
                  <span className="text-slate-400 group-open:rotate-180 transition-transform">&#9662;</span>
                </summary>
                <p className="px-5 pb-4 text-sm text-slate-600 leading-relaxed">{faq.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="border-t border-slate-100 pt-8">
          <h3 className="text-sm font-semibold text-slate-500 mb-3">Related Tools & Guides</h3>
          <div className="flex flex-wrap gap-2">
            <Link to="/" className="px-4 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:border-brand-400 hover:text-brand-600 transition-colors">Single Domain Search</Link>
            <Link to="/browse" className="px-4 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:border-brand-400 hover:text-brand-600 transition-colors">Browse by Niche</Link>
            <Link to="/marketplaces" className="px-4 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:border-brand-400 hover:text-brand-600 transition-colors">Marketplace Comparison</Link>
            <Link to="/blog/link-building-pricing" className="px-4 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:border-brand-400 hover:text-brand-600 transition-colors">Link Building Pricing 2026</Link>
            <Link to="/blog/how-much-do-backlinks-cost" className="px-4 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:border-brand-400 hover:text-brand-600 transition-colors">How Much Do Backlinks Cost?</Link>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}
